import { List } from "immutable";
import { Observable } from "micro-observables";
import { Signal } from "micro-signals";
import { Page, PaginationOptions } from "@utils/page";
import { PaginatedData } from "@core/paginatedData";
import { MappedStore, SimpleMappedStore } from "./mappedStore";
import { Store } from "./store";
import { idToItem } from "./utils";

export class SimpleMappedStorePresentation<T extends { id: string }, S extends string = string> implements MappedStore<
	T,
	S
> {
	private readonly _mappedStore: MappedStore<T, S>;

	onChange = new Signal<{ id: S; data: PaginatedData<T> | null }>();

	constructor(
		private readonly _store: Store<T>,
		fetchList: (id: S, page: PaginationOptions) => Promise<Page<T>>
	) {
		this._mappedStore = new SimpleMappedStore<T, S>(fetchList);
		this._mappedStore.onChange.add(({ data, id }) => {
			if (data === null) {
				return;
			}

			this._store.merge(data.content);
			this.onChange.dispatch({ data, id });
		});
	}

	updateContent(id: S, updater: (content: List<T>) => List<T>): void {
		this._mappedStore.updateContent(id, updater);
	}

	fetch(id: S): Promise<void> {
		return this._mappedStore.fetch(id);
	}
	fetchMore(id: S): Promise<void> {
		return this._mappedStore.fetchMore(id);
	}

	getObservable(id: S): Observable<PaginatedData<T> | null> {
		return Observable.from(this._store.itemsById, this._mappedStore.getObservable(id)).transform(
			([items, paginatedItems]) => idToItem(items, paginatedItems)
		);
	}
}

export class ComplexMappedStorePresentation<
	T extends { id: string },
	S extends Record<string, unknown> | undefined,
> implements MappedStore<T, S> {
	private readonly _mappedStore: MappedStore<T, string>;

	onChange = new Signal<{ id: S; data: PaginatedData<T> | null }>();

	constructor(
		private readonly _store: Store<T>,
		fetchList: (id: S, page: PaginationOptions) => Promise<Page<T>>
	) {
		this._mappedStore = new SimpleMappedStore<T, string>((id, page) => fetchList(JSON.parse(id), page));
		this._mappedStore.onChange.add(({ data, id }) => {
			if (data === null) {
				return;
			}

			this._store.merge(data.content);
			this.onChange.dispatch({ data, id: JSON.parse(id) });
		});
	}

	updateContent(id: S, updater: (content: List<T>) => List<T>): void {
		this._mappedStore.updateContent(JSON.stringify(id), updater);
	}

	fetch(id: S): Promise<void> {
		return this._mappedStore.fetch(JSON.stringify(id));
	}
	fetchMore(id: S): Promise<void> {
		return this._mappedStore.fetchMore(JSON.stringify(id));
	}

	getObservable(id: S): Observable<PaginatedData<T> | null> {
		return Observable.from(this._store.itemsById, this._mappedStore.getObservable(JSON.stringify(id))).transform(
			([items, paginatedItems]) => idToItem(items, paginatedItems)
		);
	}
}
